<template>
  <el-dialog
    v-model="modalVisible"
    :title="'Copiar solicitud'"
    style="width: 45%"
    center
    @close="closeModal"
  >
    <div class="dialog-content">
      <div
        v-if="localRequest.isDesactualizado && !copiaCostos"
        style="margin-bottom: 20px"
      >
        <el-alert
          title="Atención: Especificación Técnica Obsoleta"
          type="warning"
          effect="dark"
          description="Esta Solicitud fue creada a partir de una ET que actualmente se encuentra obsoleta."
          show-icon
          :closable="false"
        />
        <div
          style="
            background-color: #fdf6ec;
            padding: 15px;
            border-radius: 4px;
            margin-top: 10px;
            border: 1px solid #faecd8;
          "
        >
          <p style="font-weight: 600; margin-bottom: 12px; color: #e6a23c">
            ¿Qué versión técnica desea utilizar para la copia?
          </p>
          <el-radio-group
            v-model="opcionClonarET"
            style="display: flex; flex-direction: column; align-items: flex-start; gap: 10px"
          >
            <el-radio label="actualizar">
              <strong>Actualizar a la versión vigente</strong>
            </el-radio>
            <el-radio label="mantener">
              <strong>Mantener la versión obsoleta</strong>
            </el-radio>
          </el-radio-group>
        </div>
      </div>

      <el-row :gutter="24">
        <el-col
          :span="24"
          class="select-container"
        >
          <el-radio-group
            v-model="selectedOption"
            size="default"
          >
            <el-radio
              label="copyRequest"
              value="copyRequest"
              >Copiar solicitud</el-radio
            >
            <el-radio
              label="copyRequestAndQuote"
              value="copyRequestAndQuote"
              :disabled="!isEstadoSolicitud"
              >Copiar solicitud y cotización</el-radio
            >
          </el-radio-group>
        </el-col>
      </el-row>

      <el-row
        v-if="selectedOption === 'copyRequestAndQuote'"
        :gutter="24"
      >
        <el-col
          :span="24"
          class="custom-data-container"
        >
          <template v-if="habilitarNuevaGrillaCostos">
            <div class="custom-label">¿Cómo desea copiar la cotización?</div>
            <el-radio-group
              v-model="modoCopiaCotizacion"
              style="display: flex; flex-direction: column; align-items: flex-start; gap: 8px"
            >
              <el-radio value="tecnicos"
                >Copia de datos productivos y porcentajes comerciales</el-radio
              >
              <el-radio value="costos">Copia idéntica de costos y conceptos</el-radio>
            </el-radio-group>
          </template>
          <el-alert
            v-if="copiaCostos"
            style="margin-top: 10px"
            type="info"
            :closable="false"
            show-icon
            title="Se conserva la versión de ET y las cantidades originales."
          />
        </el-col>
      </el-row>

      <el-row :gutter="24">
        <el-col
          :span="24"
          class="custom-data-container"
        >
          <div class="custom-label">Cliente/Prospecto</div>
          <el-autocomplete
            v-model="localRequest.Cliente.Descripcion"
            size="default"
            :value-key="'Descripcion'"
            :class="{ 'input-error': !localValidations.Cliente }"
            :fetch-suggestions="handleSearch"
            clearable
            placeholder="Ingresar el cliente"
            style="width: 100%"
            @select="handleClientSelect"
            @input="handleClientChange"
            @clear="handleClientChange"
          />
        </el-col>
      </el-row>
      <el-row
        :gutter="24"
        class="custom-data-container"
      >
        <custom-input
          v-model="localRequest.Referencia"
          :label="'Descripción'"
          :spanColumn="24"
          :settings="{
            placeholder: 'Ingrese la descripción del producto',
            inputClass: { 'input-error': !localValidations.Referencia },
          }"
        ></custom-input>
      </el-row>
      <el-row :gutter="24">
        <el-col
          :span="24"
          class="custom-data-container"
        >
          <div class="custom-label">Cantidades a cotizar</div>
          <div class="quantity-inputs">
            <el-button
              class="el-button-quantity"
              type="primary"
              size="default"
              :icon="IconAdd"
              :disabled="copiaCostos"
              @click="addQuantity"
            ></el-button>
            <el-button
              type="danger"
              size="default"
              :icon="IconDelete"
              :disabled="copiaCostos"
              @click="removeQuantity"
            ></el-button>
            <div class="container-quantity-inputs">
              <custom-input-number
                v-for="(quantity, index) in localRequest.SolicitudCantidad"
                :key="index"
                v-model="localRequest.SolicitudCantidad[index].Cantidad"
                class="input-number"
                :spanColumn="6"
                :settings="{
                  placeholder: `Cantidad ${index + 1}`,
                  inputClass: { 'input-error': localValidations.Cantidades[index] === false },
                  disabled: copiaCostos,
                  min: 0,
                  precision: precisionCantidad(localRequest.CodigoUnidadVenta),
                  step: 0.01,
                  controls: false,
                }"
              ></custom-input-number>
            </div>
          </div>
        </el-col>
      </el-row>
      <div class="container">
        <custom-confirm-buttons
          :disabled="false"
          :showLoading="false"
          :confirmText="'Guardar'"
          :cancelText="'Cancelar'"
          @on-cancel="onCancel"
          @on-confirm="onConfirm"
        ></custom-confirm-buttons>
      </div>
    </div>
  </el-dialog>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  reactive,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useRequestStore } from '@/views/requests/stores/request_store';
import { useProspectStore } from '@/views/prospects/stores/prospect_store.js';
import { useAuthStore } from '@/views/auth/auth_store';
import { useConfiguracionStore } from '@/stores/configuracion-general.js';
import CustomInputNumber from '@/shared/components/custom_input_number.vue';
import CustomInput from '@/shared/components/custom_input.vue';
import CustomConfirmButtons from '@/shared/components/custom_confirm_buttons.vue';
import { useLoading } from '@/shared/composables/loading.js';
import { useMessageError } from '@/shared/composables/message_error.js';
import { useMessageSuccess } from '@/shared/composables/message_success.js';
import { ElNotification } from 'element-plus';
import IconAdd from '~icons/mdi/add';
import IconDelete from '~icons/mdi/delete';
import { obtenerCodigoUsuario } from '@/shared/utils/get_user_code.js';
import { precisionCantidad } from '@/shared/utils/cantidad_unidad_venta.js';
const authStore = useAuthStore();
const usuario = authStore.usuario();
const prospectStore = useProspectStore();
const requestStore = useRequestStore();
const { searchProspectsHTTP } = prospectStore;
const { validarSolicitudCampos, solicitud } = storeToRefs(requestStore);
const {
  cloneSolicitudHTTP,
  resetSolicitud,
  setValidacionSolicitudCantidad,
  cloneSolicitudYCotizacionHTTP,
} = requestStore;
const selectedOption = ref('copyRequest');
const opcionClonarET = ref('actualizar');
const modoCopiaCotizacion = ref('tecnicos');
const cantidadesOriginales = ref([]);
const emit = defineEmits(['closed', 'cloned']);
const modalVisible = ref(false);
const isEstadoSolicitud = computed(() => localRequest.CodigoEstado >= 2);
const { habilitarNuevaGrillaCostos } = storeToRefs(useConfiguracionStore());
const copiaCostos = computed(
  () =>
    habilitarNuevaGrillaCostos.value &&
    selectedOption.value === 'copyRequestAndQuote' &&
    modoCopiaCotizacion.value === 'costos',
);
const props = defineProps({
  selectedRow: Object,
});

const localRequest = reactive({
  Codigo: null,
  Cliente: { Descripcion: '', Codigo: null },
  Referencia: null,
  SolicitudCantidad: [],
  isDesactualizado: false,
});
const localValidations = reactive({
  Cliente: true,
  Referencia: true,
  Cantidades: [],
});

const validarCamposLocal = () => {
  let isValid = true;

  localValidations.Cliente = !!localRequest.Cliente?.Codigo;
  if (!localValidations.Cliente) isValid = false;

  localValidations.Referencia =
    !!localRequest.Referencia && String(localRequest.Referencia).trim().length > 0;
  if (!localValidations.Referencia) isValid = false;

  localValidations.Cantidades = localRequest.SolicitudCantidad.map((q) => q.Cantidad > 0);
  if (localValidations.Cantidades.includes(false) || localRequest.SolicitudCantidad.length === 0) {
    isValid = false;
  }

  return isValid;
};

watch(
  () => props.selectedRow,
  (row) => {
    if (row) {
      Object.assign(localRequest, JSON.parse(JSON.stringify(row)));
      cantidadesOriginales.value = JSON.parse(JSON.stringify(row.SolicitudCantidad || []));
    }
    opcionClonarET.value = 'actualizar';
    modoCopiaCotizacion.value = 'tecnicos';
  },
  { immediate: true },
);

watch(copiaCostos, (activo) => {
  if (activo) {
    localRequest.SolicitudCantidad = JSON.parse(JSON.stringify(cantidadesOriginales.value));
  }
});
const validacionCantidad = computed(() =>
  Array.isArray(localRequest.SolicitudCantidad)
    ? localRequest.SolicitudCantidad.map((x) => x.Cantidad > 0)
    : [],
);
watch(validacionCantidad, (nv) => setValidacionSolicitudCantidad(nv));

const closeModal = () => {
  resetSolicitud();
  selectedOption.value = 'copyRequest';
  modoCopiaCotizacion.value = 'tecnicos';
  modalVisible.value = false;
  emit('closed');
};

const handleSearch = async (query, cb) => {
  const codigoUsuario = obtenerCodigoUsuario();
  let results = [];
  if (query && query.length >= 3 && query !== 'null') {
    results = await searchProspectsHTTP(query, codigoUsuario);
  }
  cb(results);
};

const addQuantity = () => {
  const newQuantity = {
    Codigo: localRequest.SolicitudCantidad.length,
    CodigoSolicitud: 0,
    ItemCantidad: localRequest.SolicitudCantidad.length + 1,
    Cantidad: null,
  };
  localRequest.SolicitudCantidad.push(newQuantity); // Agrega un nuevo input con valor 0
};

const removeQuantity = () => {
  if (localRequest.SolicitudCantidad.length > 0) {
    localRequest.SolicitudCantidad.pop();
  }
};

const handleClientSelect = (item) => {
  localRequest.Cliente.Codigo = item.Codigo;
  localRequest.CodigoCliente = item.Codigo;
};
const handleClientChange = () => {
  localRequest.Cliente.Codigo = null;
  localRequest.CodigoCliente = null;
};
const onConfirm = async () => {
  if (!validarCamposLocal()) {
    ElNotification({
      title: 'Revisar formulario',
      message: 'Completar los campos obligatorios del formulario',
      type: 'error',
    });
    return;
  }

  const payload = {
    Cliente: localRequest.Cliente,
    Referencia: localRequest.Referencia,
    SolicitudCantidad: localRequest.SolicitudCantidad,
    UsuarioCreacion: usuario,
  };

  if (copiaCostos.value) {
    payload.copiarCostos = true;
  } else {
    payload.actualizarET = localRequest.isDesactualizado
      ? opcionClonarET.value === 'actualizar'
      : true;
  }

  try {
    useLoading().openLoading();

    let resp;
    if (selectedOption.value === 'copyRequest') {
      resp = await cloneSolicitudHTTP(localRequest.Codigo, payload);
      useMessageSuccess().open('La solicitud se ha copiado correctamente.');
    } else {
      resp = await cloneSolicitudYCotizacionHTTP(localRequest.Codigo, payload);
      useMessageSuccess().open('Solicitud y cotización copiadas correctamente.');
    }

    emit('cloned', resp);
    closeModal();
  } catch (err) {
    console.error(err);
    useMessageError().open(
      err?.response?.data?.message || 'Ha ocurrido un error al intentar copiar los datos.',
    );
  } finally {
    useLoading().closeLoading();
  }
};

const onCancel = () => {
  resetSolicitud();
  closeModal();
};
</script>

<style scoped>
.custom-label {
  font-weight: 700;
  color: #111;
}

.select-container {
  flex: 1;
  margin-right: 1px;
  /* Espacio entre el select y el botón */
  text-align: center;
}

.quantity-inputs {
  display: flex;
  /* Esto hará que los elementos se coloquen en línea */
  flex-wrap: nowrap;
  /* Evita que los elementos se envuelvan a la siguiente línea */
}

.container-quantity-inputs {
  display: flex;
  overflow-x: auto;
  width: 100%;
}

/* Estilo para el botón */
.el-button-quantity {
  flex-shrink: 0;
  /* Evita que el botón se reduzca cuando el espacio sea limitado */
  margin: 0;
}

.custom-data-container {
  margin-top: 5%;
}

.container {
  display: flex;
  justify-content: flex-end;
  /* Alinea todo el contenido al final del contenedor */
}
</style>
